import React, { memo } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { HiClock, HiOutlinePencil, HiOutlineTrash } from "react-icons/hi";
import { CustomButton } from "../../atoms/CustomButton";
import { TicketListItemProps } from "./types";
import { T_PREFIX } from "./constants";

export const TicketListItem: React.FC<TicketListItemProps> = memo(
  ({ ticket, index, onEdit, onRemove }) => {
    const { t } = useTranslation();

    const formatDate = (dateString: string) => moment(dateString).format("lll");

    const hasSaleDates = ticket.sale_start_date || ticket.sale_end_date;
    const hasOrderLimits = ticket.min_per_order && ticket.max_per_order;

    return (
      <div
        className="flex items-center justify-between gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg"
        role="listitem"
        aria-label={`${ticket.name} ticket type`}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="truncate text-sm font-semibold text-gray-900">
              {ticket.name}
            </span>
            {ticket.quantity_sold !== undefined && ticket.quantity_sold > 0 && (
              <span className="text-xs tabular-nums text-gray-500">
                {t(`${T_PREFIX}.eventCard.soldCount`, {
                  count: ticket.quantity_sold,
                })}
              </span>
            )}
          </div>

          <div className="mt-0.5 text-xs text-gray-500">
            <span className="text-gray-700">
              {ticket.price === 0
                ? t(`${T_PREFIX}.ticketForm.freeTicketBadge`)
                : `${ticket.price} ${ticket.currency}`}
            </span>
            {ticket.quantity_total ? (
              <span className="md:before:content-['_•_']">
                {t(`${T_PREFIX}.ticketSection.totalAvailable`, {
                  count: ticket.quantity_total,
                })}
              </span>
            ) : null}
            {hasOrderLimits && (
              <span className="md:before:content-['_•_']">
                {t(`${T_PREFIX}.ticketSection.perOrder`, {
                  min: ticket.min_per_order,
                  max: ticket.max_per_order,
                })}
              </span>
            )}
          </div>

          {hasSaleDates && (
            <div className="flex items-start gap-1 text-xs text-gray-500 mt-1">
              <HiClock
                size={12}
                aria-hidden="true"
                className="flex-shrink-0 mt-0.5 text-gray-400"
              />
              <div className="flex flex-col md:flex-row md:gap-1">
                {ticket.sale_start_date && (
                  <span>
                    {t(`${T_PREFIX}.ticketSection.saleFrom`)}{" "}
                    {formatDate(ticket.sale_start_date)}
                  </span>
                )}
                {ticket.sale_start_date && ticket.sale_end_date && (
                  <span className="hidden md:inline">•</span>
                )}
                {ticket.sale_end_date && (
                  <span>
                    {t(`${T_PREFIX}.ticketSection.saleTo`)}{" "}
                    {formatDate(ticket.sale_end_date)}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {(onEdit || onRemove) && (
          <div className="flex gap-2">
            {onEdit && (
              <CustomButton
                type="button"
                variant="none"
                size="icon"
                onClick={() => onEdit(ticket.ticket_id)}
                className="rounded-md text-gray-400 transition-colors enabled:hover:text-blue-600"
                aria-label={`Edit ${ticket.name} ticket`}
                icon={<HiOutlinePencil size={18} />}
              />
            )}
            {onRemove && (
              <CustomButton
                type="button"
                variant="none"
                size="icon"
                onClick={() => onRemove(ticket.ticket_id)}
                className="rounded-md text-gray-400 transition-colors enabled:hover:text-red-600"
                aria-label={`Remove ${ticket.name} ticket`}
                icon={<HiOutlineTrash size={18} />}
              />
            )}
          </div>
        )}
      </div>
    );
  }
);

TicketListItem.displayName = "TicketListItem";
